import { useState } from 'react';
import {
  CalendarDays,
  ClipboardList,
  FileCheck2,
  RefreshCw,
  ShieldCheck,
  Users,
  Wallet,
  AlertTriangle,
} from 'lucide-react';

import { DetailSummarySidebar } from '@/ui/DetailSummarySidebar';
import { DetailWithTimeline } from '@/ui/DetailWithTimeline';
import { HeaderInfoRow } from '@/ui/HeaderInfoRow';
import { HistoryTimeline, type HistoryTimelineEntry } from '@/ui/HistoryTimeline';
import { NextStepBanner } from '@/ui/NextStepBanner';
import { PageBanner } from '@/ui/PageBanner';
import { ProgressTimeline, type PassoDeProgresso } from '@/ui/ProgressTimeline';
import { StatsGrid } from '@/ui/StatsGrid';
import { StatusBadge } from '@/ui/StatusBadge';
import { TimelineBadgeStepper, type TimelineStep } from '@/ui/TimelineBadgeStepper';
import { TimelineNextStepBanner } from '@/ui/TimelineNextStepBanner';
import { Button } from '@/ui/base/button';
import { Card, CardContent } from '@/ui/base/card';

import { AmostraSuspensa } from '../AmostraSuspensa';
import { ComponentShowcase } from '../ComponentShowcase';
import { INDICADORES_DE_EXEMPLO, OS_DE_EXEMPLO } from '../exemplos';

const DIA = 24 * 60 * 60 * 1000;

/** Data ISO de N dias atrás: a linha do tempo de exemplo acompanha o calendário. */
function diasAtras(agora: number, n: number): string {
  return new Date(agora - n * DIA).toISOString();
}

function dataCurta(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR');
}

const ETAPAS: TimelineStep[] = [
  { sigla: 'INS', label: 'Inscrição', status: 'completed', responsavel: 'Credenciado' },
  { sigla: 'TRI', label: 'Triagem', status: 'completed', responsavel: 'DTP' },
  {
    sigla: 'DOC',
    label: 'Documentação',
    status: 'current',
    responsavel: 'Credenciado',
    slaLabel: '5 dias úteis',
    proximaAcao: 'Enviar a apólice de seguro',
  },
  { sigla: 'TER', label: 'Termo de Adesão', status: 'pending' },
  { sigla: 'OS', label: 'Ordem de Serviço', status: 'pending' },
];

const ICONES = [Users, ClipboardList, AlertTriangle, Wallet];

/** Cabeçalho, aviso, detalhe e linha do tempo: as peças das telas de detalhe. */
export function AmostrasDeDetalhe() {
  const [agora] = useState(() => Date.now());
  const [comAviso, definirComAviso] = useState(false);

  const passos: PassoDeProgresso[] = [
    {
      label: 'Inscrição recebida',
      status: 'completed',
      date: diasAtras(agora, 12),
      origem: 'Credenciado',
    },
    {
      label: 'Documentação conferida',
      status: 'completed',
      date: diasAtras(agora, 6),
      origem: 'DTP',
      documento: { label: 'Parecer da conferência' },
    },
    {
      label: 'Assinatura do Termo',
      status: 'current',
      date: diasAtras(agora, 1),
      origem: 'Credenciado',
      pendencia: { label: 'Prazo de assinatura em 3 dias', severidade: 'media' },
      proximaAcao: 'Assinar o Termo de Adesão pelo gov.br.',
    },
    { label: 'Emissão da Ordem de Serviço', status: 'pending' },
  ];

  const historico: HistoryTimelineEntry[] = [
    {
      id: 'h1',
      icon: RefreshCw,
      tone: 'bg-primary/10 text-primary',
      titulo: 'Mudança de situação',
      etiqueta: 'Situação',
      quando: dataCurta(diasAtras(agora, 2)),
      por: 'Pessoa Exemplo Dois',
      de: 'Pendente',
      para: 'Ativo',
    },
    {
      id: 'h2',
      icon: FileCheck2,
      tone: 'bg-muted text-muted-foreground',
      titulo: 'Documento enviado',
      quando: dataCurta(diasAtras(agora, 5)),
      por: 'Pessoa Exemplo Um',
      descricao: 'CRLV do veículo',
    },
  ];

  return (
    <>
      <ComponentShowcase
        id="page-header"
        title="PageHeader"
        description="Cabeçalho de toda página: o único h1, a descrição, as ações à direita e, logo abaixo, o espaço onde os PageBanner aparecem. É sempre o primeiro elemento visual da tela."
        code={`<PageHeader title="Credenciados" description="Transportadores credenciados" eyebrow="Operação">
  <Button>Exportar CSV</Button>
</PageHeader>`}
        propsTable={[
          ['title', 'string', '-', 'Título da página (o h1)'],
          ['description', 'ReactNode', '-', 'Frase abaixo do título'],
          ['eyebrow', 'string', '-', 'Texto curto acima do título (módulo, etapa)'],
          ['children', 'ReactNode', '-', 'Ações à direita do título'],
        ]}
      >
        <p className="text-sm text-muted-foreground">
          O exemplo real é o cabeçalho no alto desta página — “Design System”, a descrição e o botão
          Baixar PDF. Uma segunda amostra aqui daria à página dois h1 e dois espaços de aviso com o
          mesmo id.
        </p>
      </ComponentShowcase>

      <ComponentShowcase
        id="page-banner"
        title="PageBanner"
        description="Aviso da página: sobe sozinho para o espaço abaixo do título do PageHeader, nunca acima dele, em informação, atenção, erro ou sucesso. Sem PageHeader na página, aparece onde foi posto."
        code={`<PageBanner
  tone="warning"
  title="Competência em fechamento"
  description="Apontamentos novos entram na próxima competência."
/>`}
        propsTable={[
          ['title', 'ReactNode', '-', 'Título do aviso'],
          ['description', 'ReactNode', '-', 'Texto do aviso'],
          ['tone', '"info" | "warning" | "destructive" | "success"', '"info"', 'Tom do aviso'],
          ['icon', 'LucideIcon', 'pelo tom', 'Ícone próprio'],
          ['children', 'ReactNode', '-', 'Conteúdo a mais (ex.: um botão)'],
          ['className', 'string', '-', 'Classes do aviso'],
        ]}
      >
        <div className="flex flex-col items-start gap-2">
          <Button
            variant="outline"
            size="sm"
            aria-pressed={comAviso}
            onClick={() => {
              definirComAviso((v) => !v);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            {comAviso ? 'Tirar o aviso do alto da página' : 'Mostrar o aviso no alto da página'}
          </Button>
          <p className="text-xs text-muted-foreground">
            O aviso aparece abaixo do título “Design System”, no alto desta página, como numa tela
            de verdade.
          </p>
          {comAviso && (
            <PageBanner
              tone="warning"
              title="Aviso de exemplo"
              description="Este é um PageBanner de verdade, posto pela amostra da seção Componentes do sistema."
            />
          )}
        </div>
      </ComponentShowcase>

      <ComponentShowcase
        id="stats-grid"
        title="StatsGrid"
        description="Grade de StatCard dos painéis: duas colunas no celular, quatro a partir de md. Os números são do universo inteiro e não mudam com os filtros da tabela."
        code={`<StatsGrid
  stats={[
    { title: 'Credenciados ativos', value: 1284, icon: Users },
    { title: 'Pendências', value: 37, icon: AlertTriangle, href: '/operacao/pendencias' },
  ]}
/>`}
        propsTable={[
          [
            'stats',
            'StatItem[]',
            '-',
            'Indicadores: title, value, icon, description?, trend?, href?, onClick?, active?',
          ],
          ['columns', 'string', '"grid-cols-2 md:grid-cols-4"', 'Colunas da grade'],
          ['className', 'string', '-', 'Classes da grade'],
        ]}
      >
        <div className="w-full">
          <StatsGrid
            // Uma coluna no celular: valor longo (R$ 4.812.930,00) não cabe em meia largura de 375 px.
            columns="grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
            stats={INDICADORES_DE_EXEMPLO.map((k, i) => ({
              title: k.label,
              value: k.value,
              icon: ICONES[i] ?? Users,
              description: k.hint,
            }))}
          />
        </div>
      </ComponentShowcase>

      <ComponentShowcase
        id="header-info-row"
        title="HeaderInfoRow"
        description="Linha de informação do cabeçalho de um detalhe: rótulo fixo à esquerda, com explicação opcional na dica, e o conteúdo à direita."
        code={`<HeaderInfoRow icon={CalendarDays} label="Vigência" description="Período do Termo de Adesão">
  01/02 a 31/12
</HeaderInfoRow>`}
        propsTable={[
          ['icon', 'LucideIcon', '-', 'Ícone do rótulo'],
          ['label', 'string', '-', 'Rótulo da linha'],
          ['description', 'string', '-', 'Explicação do rótulo, na dica'],
          ['children', 'ReactNode', '-', 'O conteúdo da linha'],
          ['className', 'string', '-', 'Classes da linha'],
        ]}
      >
        <div className="w-full space-y-2">
          <HeaderInfoRow
            icon={ShieldCheck}
            label="Situação"
            description="Situação atual do credenciado"
          >
            <StatusBadge status="ativo" />
          </HeaderInfoRow>
          <HeaderInfoRow icon={CalendarDays} label="Ordem de Serviço">
            <span className="font-mono text-sm">{OS_DE_EXEMPLO.numero}</span>
          </HeaderInfoRow>
        </div>
      </ComponentShowcase>

      <ComponentShowcase
        id="detail-with-timeline"
        title="DetailWithTimeline"
        description="Moldura das telas de detalhe: conteúdo principal na coluna larga e a barra lateral (resumo, linha do tempo) à direita a partir de lg, fixa ao rolar; no celular, a barra vem depois do conteúdo."
        code={`<DetailWithTimeline timeline={<DetailSummarySidebar items={resumo} audit={auditoria} />}>
  <Tabs>…</Tabs>
</DetailWithTimeline>`}
        propsTable={[
          ['children', 'ReactNode', '-', 'Conteúdo principal'],
          ['timeline', 'ReactNode', '-', 'Barra lateral (tipicamente DetailSummarySidebar)'],
          ['summary', 'ReactNode', '-', 'Cartão-resumo em largura total, acima da grade'],
          ['side', '"left" | "right"', '"right"', 'Lado da barra lateral a partir de lg'],
          ['asideWidth', '280 | 320', '320', 'Largura da barra a partir de xl'],
          ['className', 'string', '-', 'Classes do contêiner'],
          ['contentClassName', 'string', '-', 'Classes da coluna principal'],
        ]}
      >
        <div className="w-full">
          <DetailWithTimeline
            timeline={
              <DetailSummarySidebar
                items={[
                  { label: 'Número', value: OS_DE_EXEMPLO.numero },
                  { label: 'Situação', value: <StatusBadge status="ativo" label="Vigente" /> },
                  { label: 'Placa', value: OS_DE_EXEMPLO.placa },
                ]}
              />
            }
          >
            <Card>
              <CardContent className="p-4 text-sm text-muted-foreground">
                Conteúdo principal do detalhe (abas, dados, documentos).
              </CardContent>
            </Card>
          </DetailWithTimeline>
        </div>
      </ComponentShowcase>

      <ComponentShowcase
        id="detail-summary-sidebar"
        title="DetailSummarySidebar"
        description="Cartão lateral do detalhe: o resumo (rótulo e valor; item vazio some) e a auditoria — criado e atualizado em, e por quem. É a barra lateral da amostra acima."
        code={`<DetailSummarySidebar
  items={[{ label: 'Número', value: os.numero }, os.placa && { label: 'Placa', value: os.placa }]}
  audit={{ createdAt: os.created_at, updatedAt: os.updated_at, updatedBy: os.atualizado_por }}
/>`}
        propsTable={[
          ['title', 'string', '"Resumo"', 'Título do cartão'],
          [
            'items',
            'Array<SummaryItem | null | false>',
            '-',
            'Rótulo e valor; nulo, falso ou vazio é filtrado',
          ],
          [
            'audit',
            '{ createdAt?, updatedAt?, createdBy?, updatedBy? }',
            '-',
            'Datas (Date ou ISO) e autores',
          ],
          ['extra', 'ReactNode', '-', 'Conteúdo abaixo da auditoria (ex.: ações)'],
        ]}
      >
        <div className="w-full space-y-2">
          <p className="text-sm text-muted-foreground">
            O resumo está montado na amostra do DetailWithTimeline, logo acima.
          </p>
          <AmostraSuspensa item="detail-summary-sidebar" />
        </div>
      </ComponentShowcase>

      <ComponentShowcase
        id="progress-timeline"
        title="ProgressTimeline"
        description="Andamento de um processo em etapas: concluída, atual, pendente, com erro, com atenção ou pulada; cada etapa com data, origem, documento e pendência. A próxima ação só aparece na etapa atual."
        code={`<ProgressTimeline passos={passos} carregando={consulta.isPending} />`}
        propsTable={[
          [
            'passos',
            'PassoDeProgresso[]',
            '-',
            'label, status, date?, description?, origem?, documento?, pendencia?, proximaAcao?',
          ],
          ['carregando', 'boolean', 'false', 'Esqueleto de três etapas'],
          ['mensagemVazia', 'ReactNode', '-', 'O que mostrar sem passos (padrão: nada)'],
          ['variante', '"progresso" | "simples"', '"progresso"', 'Destaque da etapa atual'],
          ['className', 'string', '-', 'Classes da lista'],
        ]}
      >
        <div className="w-full max-w-lg">
          <ProgressTimeline passos={passos} />
        </div>
      </ComponentShowcase>

      <ComponentShowcase
        id="timeline-next-step-banner"
        title="TimelineNextStepBanner"
        description="Lê a ProgressTimeline da tela e mostra o próximo passo da etapa atual (proximaAcao). Sem etapa atual com próxima ação, não aparece."
        code={`<TimelineNextStepBanner passos={passos} />`}
        propsTable={[
          ['passos', 'PassoDeProgresso[]', '-', 'Os mesmos passos da ProgressTimeline'],
          ['actionLabel', 'string', '-', 'Botão de ação (opcional)'],
          ['onAction', '() => void', '-', 'Ação do botão; sem ela, só informa'],
          ['title', 'string', '"Próximo passo: {etapa}"', 'Título próprio'],
          ['sticky', 'boolean', 'false', 'Fixo no topo do contêiner'],
          ['variant', '"info" | "success"', '"info"', 'Tom'],
          ['className', 'string', '-', 'Classes do aviso'],
        ]}
      >
        <div className="w-full">
          <TimelineNextStepBanner passos={passos} />
        </div>
      </ComponentShowcase>

      <ComponentShowcase
        id="next-step-banner"
        title="NextStepBanner"
        description="Conduz ao próximo passo natural depois de uma ação que mudou o registro (aprovação, fechamento, substituição). O título é um verbo no imperativo."
        code={`<NextStepBanner
  title="Próximo passo: emitir a Ordem de Serviço"
  description="O Termo foi assinado; a OS pode ser emitida."
/>`}
        propsTable={[
          ['title', 'string', '-', 'Título, com verbo no imperativo'],
          ['description', 'ReactNode', '-', 'O que fazer e por quê'],
          ['actionLabel', 'string', '-', 'Botão de ação'],
          ['onAction', '() => void', '-', 'Ação do botão'],
          ['variant', '"info" | "success"', '"info"', 'success para passo opcional'],
          ['sticky', 'boolean', 'false', 'Fixo no topo do contêiner'],
          ['icon', 'ReactNode', 'ArrowRight', 'Ícone próprio'],
          ['className', 'string', '-', 'Classes do aviso'],
        ]}
      >
        <div className="w-full space-y-3">
          <NextStepBanner
            title="Próximo passo: emitir a Ordem de Serviço"
            description="O Termo de Adesão de exemplo foi assinado; a OS pode ser emitida."
          />
          <NextStepBanner
            variant="success"
            title="Opcional: avisar o credenciado"
            description="A mensagem sai pelo canal escolhido nas preferências dele."
          />
        </div>
      </ComponentShowcase>

      <ComponentShowcase
        id="timeline-badge-stepper"
        title="TimelineBadgeStepper"
        description="Etapas em siglas lado a lado, com o nome, o responsável e o prazo na dica de cada uma. Com onStepClick, cada sigla vira botão."
        code={`<TimelineBadgeStepper steps={etapas} onStepClick={() => abrirLinhaDoTempo()} />`}
        propsTable={[
          [
            'steps',
            'TimelineStep[]',
            '-',
            'sigla, label, status, responsavel?, slaLabel?, proximaAcao?',
          ],
          ['onStepClick', '() => void', '-', 'Torna cada sigla um botão'],
        ]}
      >
        <TimelineBadgeStepper steps={ETAPAS} />
      </ComponentShowcase>

      <ComponentShowcase
        id="history-timeline"
        title="HistoryTimeline"
        description="Histórico de eventos de um registro: quem fez o quê e quando, com “de → para” nas mudanças. Sem eventos, mostra o estado vazio."
        code={`<HistoryTimeline entradas={eventos} vazioTitulo="Nenhum evento registrado" />`}
        propsTable={[
          [
            'entradas',
            'HistoryTimelineEntry[]',
            '-',
            'id, icon, tone, titulo, etiqueta?, quando, por?, descricao?, de?, para?',
          ],
          ['vazioTitulo', 'string', '-', 'Título do estado vazio'],
          ['vazioDescricao', 'string', '-', 'Texto do estado vazio'],
        ]}
      >
        <div className="w-full max-w-lg">
          <HistoryTimeline entradas={historico} />
        </div>
      </ComponentShowcase>
    </>
  );
}
